import { InputNumber } from "@/components/InputNumber";
import { datePickerFormItemProps, datePickerStandardProps, selectStandardProps } from "@/utils/componentUtils";
import { validations } from "@/utils/validationUtils";
import { Checkbox, Col, DatePicker, Form, Row, Select } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { rowGutter } from "../../../../../../../common/constants";
import { GapDuration } from "../../../../../../calculator/calcs/vehicle/enums";
import ComplicitySelect from "../../../../../../enumerations/components/form/ComplicitySelect";
import { CoverageLimitSelect } from "../../../../../../enumerations/components/form/CoverageLimitSelect";
import { InsuranceType } from "../../../../../enums";
import { CreateUpdateInsuranceContract } from "../../../../../types";
import { StandardVehicleInsuranceFormPart } from "./StandardVehicleInsuranceFormPart";

const colSpan = 4;

type Props = {
  index: number;
  formNameDataPrefix: (string | number)[];
  formNameCoveragesPrefix: (string | number)[];
  formNamePrefix: (string | number)[];
  formNameMtplPrefix: (string | number)[];
};

export const CrashInsuranceFormPart = ({
  formNameDataPrefix,
  formNamePrefix,
  formNameCoveragesPrefix,
  formNameMtplPrefix,
  index,
}: Props) => {
  const { t } = useTranslation();

  const form = Form.useFormInstance<CreateUpdateInsuranceContract>();
  const institutionId = Form.useWatch("institutionId", form);
  const effectiveBeginningDate = Form.useWatch("effectiveBeginningDate", form);
  const gap = Form.useWatch<boolean | undefined>([...formNameCoveragesPrefix, "gap"], form);
  const contractEntryDate = Form.useWatch<string | undefined>([...formNamePrefix, "contractEntryDate"], form);
  const type = Form.useWatch<InsuranceType | undefined>([...formNamePrefix, "type"], form);

  const resolveEntryDateLabel = (type?: InsuranceType) => {
    return type === InsuranceType.LIFE
      ? t("contract.attrs.insurances.contractEntryDateLife")
      : t("contract.attrs.insurances.contractEntryDate");
  };

  return (
    <>
      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name={[...formNameDataPrefix, "price"]}
            label={t("contract.attrs.insurances.insuranceData.price")}
            rules={[validations.minNumber(1)]}
          >
            <InputNumber type="decimal" addon="euro" />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <ComplicitySelect
            formItemProps={{
              name: [...formNamePrefix, "complicityId"],
              label: t("contract.attrs.insurances.complicityId"),
              rules: [validations.notNull],
            }}
            institutionId={institutionId}
          />
        </Col>

        <StandardVehicleInsuranceFormPart formNameDataPrefix={formNameDataPrefix} index={index} />
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name={[...formNameCoveragesPrefix, "extendedAssistance"]}
            valuePropName="checked"
            rules={[validations.none]}
            initialValue={false}
          >
            <Checkbox>{t("contract.attrs.insurances.insuranceData.coverages.extendedAssistance")}</Checkbox>
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={[...formNameCoveragesPrefix, "glass"]}
            valuePropName="checked"
            rules={[validations.none]}
            initialValue={false}
          >
            <Checkbox>{t("contract.attrs.insurances.insuranceData.coverages.glass")}</Checkbox>
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={[...formNameCoveragesPrefix, "replacementVehicle"]}
            valuePropName="checked"
            rules={[validations.none]}
            initialValue={false}
          >
            <Checkbox>{t("contract.attrs.insurances.insuranceData.coverages.replacementVehicle")}</Checkbox>
          </Form.Item>
        </Col>

        <Col span={colSpan * 2}>
          <Form.Item
            name={[...formNameCoveragesPrefix, "abroadVehicleRepair"]}
            valuePropName="checked"
            rules={[validations.none]}
            initialValue={false}
          >
            <Checkbox>{t("contract.attrs.insurances.insuranceData.coverages.abroadVehicleRepair")}</Checkbox>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name={[...formNameCoveragesPrefix, "gap"]}
            valuePropName="checked"
            rules={[validations.none]}
            initialValue={false}
            className={gap ? "form-item-without-label" : undefined}
          >
            <Checkbox>{t("contract.attrs.insurances.insuranceData.coverages.gap")}</Checkbox>
          </Form.Item>
        </Col>

        {gap && (
          <>
            <Col span={colSpan}>
              <Form.Item
                name={[...formNameCoveragesPrefix, "gapDuration"]}
                label={t("calc.vehicle.enums.gapDuration._label")}
                rules={[validations.none]}
              >
                <Select
                  {...selectStandardProps}
                  allowClear
                  options={Object.keys(GapDuration).map((duration) => ({
                    value: duration,
                    label: t("calc.vehicle.enums.gapDuration." + duration),
                  }))}
                />
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              <Form.Item
                name={[...formNameCoveragesPrefix, "gapComplicityReinsurance"]}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
                className="form-item-without-label"
              >
                <Checkbox>{t("contract.attrs.insurances.insuranceData.coverages.gapComplicityReinsurance")}</Checkbox>
              </Form.Item>
            </Col>
          </>
        )}
      </Row>

      <Row gutter={rowGutter}>
        <Col span={24}>
          <b>{t("contract.attrs.insurances.insuranceData.mtpl._label")}</b>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <CoverageLimitSelect
            formItemProps={{
              name: [...formNamePrefix, "coverageLimitId"],
              label: t("contract.attrs.insurances.coverageLimitId"),
              rules: [validations.none],
            }}
            institutionId={institutionId}
            selectProps={{ allowClear: true }}
          />
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={[...formNameMtplPrefix, "effectiveBeginningDate"]}
            label={t("contract.attrs.insurances.insuranceData.mtpl.effectiveBeginningDate")}
            rules={[
              contractEntryDate
                ? validations.notBefore(contractEntryDate, resolveEntryDateLabel(type))
                : effectiveBeginningDate
                  ? validations.notBefore(effectiveBeginningDate, t("contract.attrs.effectiveBeginningDate"))
                  : validations.none,
            ]}
            {...datePickerFormItemProps}
          >
            <DatePicker
              {...datePickerStandardProps}
              allowClear
              minDate={
                contractEntryDate
                  ? dayjs(contractEntryDate)
                  : effectiveBeginningDate
                    ? dayjs(effectiveBeginningDate)
                    : undefined
              }
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};
